import React from 'react';
import { Box, Dialog, IconButton, Typography } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import TimelineDetailRows from './TimelineDetailRows';
import CaptureBundleTimelineText, { splitCaptureBundleTimelineText } from './CaptureBundleTimelineText';

const TEXT_PRIMARY = '#1F2937';
const TEXT_MUTED = '#6B7280';

const normalizeAttachment = (value) => {
  if (!value) {
    return null;
  }

  if (typeof value === 'string') {
    const url = value.trim();
    if (!url || /\.(webm|m4a|mp3|wav|aac|ogg)(?:\?|$)/i.test(url.toLowerCase())) return null;
    return { url, type: 'image' };
  }

  if (typeof value === 'object') {
    const url = String(value.url || value.downloadURL || value.mediaURL || '').trim();
    if (!url) {
      return null;
    }

    return {
      ...value,
      url,
      type: String(value.type || value.mediaType || '').toLowerCase() || 'image',
    };
  }

  return null;
};

const getPrimaryImageAttachment = (mediaUrls = []) => {
  const attachments = Array.isArray(mediaUrls)
    ? mediaUrls.map(normalizeAttachment).filter(Boolean)
    : [];

  return attachments.find((item) => {
    const type = String(item.type || '').toLowerCase();
    return type === 'image' || type.startsWith('image/');
  }) || null;
};

const DefaultTimelineBody = ({
  summaryText = '',
  insight = '',
  notesText = '',
  detailRows = [],
  mediaUrls = [],
  audioAttachments = [],
  nested = false,
  isMedicationRow = false,
  isMedicationChangeRow = false,
  compact = false,
  summaryLines = 2,
  reviewBanner = null,
  story = false,
  notesAsMetadata = false,
}) => {
  const [openImage, setOpenImage] = React.useState(false);
  const safeSummary = String(summaryText || '').trim();
  const captureBundleSections = splitCaptureBundleTimelineText(safeSummary);
  const safeInsight = String(insight || '').trim();
  const safeNotes = String(notesText || '').trim();
  const notesShouldRender = Boolean(safeNotes && safeNotes !== safeSummary);
  const safeRows = Array.isArray(detailRows) ? detailRows.filter(Boolean) : [];
  const primaryImageAttachment = getPrimaryImageAttachment(mediaUrls);
  const primaryAudioAttachment = (Array.isArray(audioAttachments) ? audioAttachments : [])
    .map(normalizeAttachment)
    .find((item) => {
      const type = String(item?.type || '').toLowerCase();
      return type === 'audio' || type.startsWith('audio/') || /\.(webm|m4a|mp3|wav|aac|ogg)(?:\?|$)/i.test(item?.url || '');
    });
  const safeReviewBanner = reviewBanner && typeof reviewBanner === 'object' ? reviewBanner : null;
  const hasBodyContent = Boolean(safeSummary || safeInsight || notesShouldRender || safeRows.length || primaryImageAttachment || primaryAudioAttachment);
  const resolvedSummaryLines = Number.isFinite(summaryLines) ? summaryLines : 2;

  if (!hasBodyContent) {
    return null;
  }

  return (
    <Box
      sx={{
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: compact ? 0.4 : 0.5,
        mt: 0,
        pt: 0,
      }}
    >
      {safeReviewBanner ? (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'flex-start',
            gap: 0.7,
            px: 0.95,
            py: 0.7,
            borderRadius: '12px',
            bgcolor: '#FFF1F1',
            border: '1px solid #F7C0C0',
            boxShadow: 'inset 0 0 0 1px rgba(180, 72, 72, 0.08)',
            mb: 0.5,
            maxWidth: '100%',
          }}
        >
          <Box
            sx={{
              width: 18,
              height: 18,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#B45050',
              mt: 0.1,
            }}
          >
            <WarningAmberOutlinedIcon sx={{ fontSize: 14 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.78rem', lineHeight: 1.1, fontWeight: 700, color: '#B45050' }}>
              {safeReviewBanner.label || 'Allergy concern flagged'}
            </Typography>
            <Typography sx={{ fontSize: '0.68rem', lineHeight: 1.18, fontWeight: 500, color: '#B45050', mt: 0.12 }}>
              {safeReviewBanner.message || ''}
            </Typography>
          </Box>
        </Box>
      ) : null}

      {captureBundleSections ? (
        <CaptureBundleTimelineText sections={captureBundleSections} />
      ) : safeSummary ? (
      <Typography
        variant="body2"
        sx={{
          color: story ? '#4A5954' : TEXT_PRIMARY,
          fontSize: story ? { xs: '0.92rem', sm: '0.99rem' } : (nested ? '0.82rem' : ((isMedicationRow || isMedicationChangeRow) ? '0.86rem' : '0.9rem')),
          lineHeight: story ? 1.58 : 1.4,
          fontWeight: 400,
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          display: resolvedSummaryLines > 0 ? '-webkit-box' : 'block',
          WebkitLineClamp: resolvedSummaryLines > 0
            ? (nested ? 1 : ((isMedicationRow || isMedicationChangeRow) ? 1 : resolvedSummaryLines))
            : undefined,
          WebkitBoxOrient: resolvedSummaryLines > 0 ? 'vertical' : undefined,
          overflow: resolvedSummaryLines > 0 ? 'hidden' : 'visible',
          mt: 0,
          mb: 0,
          pt: 0,
        }}
      >
          {safeSummary}
        </Typography>
      ) : null}

      {safeInsight ? (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            alignSelf: 'flex-start',
            px: 0.56,
            py: 0.1,
            borderRadius: 0.5,
            bgcolor: 'rgba(250, 204, 21, 0.18)',
            color: TEXT_MUTED,
            maxWidth: '100%',
            mt: 0,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.64rem',
              lineHeight: 1.12,
              fontStyle: 'italic',
              color: TEXT_MUTED,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              mt: 0,
              mb: 0,
            }}
        >
          {safeInsight}
        </Typography>
      </Box>
      ) : null}

      {notesShouldRender ? (
        <Typography
          variant="caption"
          sx={{
            fontSize: notesAsMetadata ? '0.7rem' : (story ? { xs: '0.87rem', sm: '0.93rem' } : '0.64rem'),
            lineHeight: notesAsMetadata ? 1.25 : (story ? 1.52 : 1.12),
            fontWeight: notesAsMetadata ? 650 : (story ? 400 : 700),
            color: notesAsMetadata ? TEXT_MUTED : (story ? '#4A5954' : TEXT_MUTED),
            mt: notesAsMetadata ? 0.2 : 0,
            mb: 0,
          }}
        >
          {safeNotes}
        </Typography>
      ) : null}

      {primaryImageAttachment ? (
        <>
        <Box
          component="button"
          type="button"
          aria-label="Open attached photo"
          onClick={() => setOpenImage(true)}
          sx={{
            mt: notesShouldRender ? 0.6 : 0,
            width: '100%',
            maxWidth: story ? 320 : 260,
            borderRadius: story ? '18px' : '14px',
            overflow: 'hidden',
            border: '1px solid rgba(148, 163, 184, 0.22)',
            bgcolor: '#F8FAFC',
            boxShadow: story ? '0 10px 24px rgba(15, 23, 42, 0.08)' : '0 6px 14px rgba(15, 23, 42, 0.06)',
            p: 0,
            cursor: 'pointer',
            textAlign: 'inherit',
          }}
        >
          <Box
            component="img"
            src={primaryImageAttachment.url}
            alt={primaryImageAttachment.filename || 'Attached photo'}
            sx={{
              width: '100%',
              display: 'block',
              objectFit: 'cover',
              aspectRatio: '4 / 3',
            }}
          />
        </Box>
        <Dialog open={openImage} onClose={() => setOpenImage(false)} maxWidth="lg" PaperProps={{ sx: { bgcolor: '#111827', borderRadius: '18px', overflow: 'hidden' } }}>
          <IconButton
            aria-label="Close photo"
            onClick={() => setOpenImage(false)}
            sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1, bgcolor: 'rgba(255,255,255,0.9)', color: '#1F2937', '&:hover': { bgcolor: '#fff' } }}
          >
            <CloseRoundedIcon />
          </IconButton>
          <Box
            component="img"
            src={primaryImageAttachment.url}
            alt={primaryImageAttachment.filename || primaryImageAttachment.fileName || 'Attached photo'}
            sx={{ display: 'block', maxWidth: '90vw', maxHeight: '88vh', objectFit: 'contain' }}
          />
        </Dialog>
        </>
      ) : null}

      {primaryAudioAttachment ? (
        <Box
          sx={{
            mt: primaryImageAttachment ? 0.65 : 0.35,
            px: 1,
            py: 0.8,
            borderRadius: '14px',
            border: '1px solid rgba(148, 163, 184, 0.22)',
            bgcolor: '#F8FAFC',
          }}
        >
          <Typography sx={{ mb: 0.45, fontSize: '0.72rem', fontWeight: 700, color: TEXT_MUTED }}>
            Original voice note
          </Typography>
          <Box
            component="audio"
            controls
            aria-label="Play original voice recording"
            src={primaryAudioAttachment.url}
            sx={{ width: '100%', display: 'block', height: 36 }}
          />
        </Box>
      ) : null}

      <TimelineDetailRows rows={safeRows} gap={compact ? 0.08 : 0.1} />
    </Box>
  );
};

export default React.memo(DefaultTimelineBody);
